import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AvaliacaoService, ConfiguracaoAvaliacao } from '../../../core/config/services/avaliacao.service';
import { ConfigService } from '../../../core/config/services/config.service';
import { ErrorHandlerService } from '../../../core/error-handling/services/error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class DesempenhoConfigService {
  constructor(
    private avaliacaoService: AvaliacaoService,
    private configService: ConfigService,
    private errorHandler: ErrorHandlerService
  ) {}

  loadConfiguracaoAvaliacao(
    turmaId: number,
    destroy$: Subject<void>,
    onConfigLoaded: (config: ConfiguracaoAvaliacao) => void,
    onError?: (error: unknown) => void
  ): void {
    if (!turmaId) {
      return;
    }

    this.avaliacaoService.getConfiguracaoAvaliacao(turmaId)
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (config) => {
          onConfigLoaded(config);
        },
        error: (error) => {
          // Se for 500, pode ser que a turma não tenha configuração ainda
          if ((error as { status?: number }).status !== 500) {
            this.errorHandler.handleError(error);
          }
          if (onError) {
            onError(error);
          }
        }
      });
  }

  createConfiguracaoCha(configuracaoAvaliacao: ConfiguracaoAvaliacao): Record<string, { visivel: boolean }> {
    return {
      COMPETENCIA: { visivel: configuracaoAvaliacao.COMPETENCIA?.visivel || false },
      CONHECIMENTO: { visivel: configuracaoAvaliacao.CONHECIMENTO?.visivel || false },
      HABILIDADE: { visivel: configuracaoAvaliacao.HABILIDADE?.visivel || false },
      ATITUDE: { visivel: configuracaoAvaliacao.ATITUDE?.visivel || false }
    };
  }

  loadEnums(
    destroy$: Subject<void>,
    onEnumLoaded: (enumName: string, data: Record<string, { description?: string; value?: unknown; hex?: string }>) => void
  ): void {
    this.loadEnum('SituacaoMatricula', destroy$, (data) => onEnumLoaded('SituacaoMatricula', data));
    this.loadEnum('SituacaoItemEducacional', destroy$, (data) => onEnumLoaded('SituacaoItemEducacional', data));
    this.loadEnum('GrupoEnturmacaoCor', destroy$, (data) => {
      const normalized = this.normalizeHexColors(data);
      onEnumLoaded('GrupoEnturmacaoCor', normalized);
    });
  }

  private loadEnum(
    enumName: string,
    destroy$: Subject<void>,
    onSuccess: (data: Record<string, { description?: string; value?: unknown; hex?: string }>) => void
  ): void {
    this.configService.getEnum(enumName)
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (data) => {
          onSuccess(data || {});
        },
        error: () => {
          onSuccess({});
        }
      });
  }

  /**
   * Normaliza cores hexadecimais adicionando # se necessário
   */
  normalizeHexColors(
    grupoEnturmacaoCor: Record<string, { description?: string; value?: unknown; hex?: string }>
  ): Record<string, { description?: string; value?: unknown; hex?: string }> {
    const normalized: Record<string, { description?: string; value?: unknown; hex?: string }> = {};
    
    Object.keys(grupoEnturmacaoCor).forEach(key => {
      const color = grupoEnturmacaoCor[key];
      const hex = color.hex;
      
      normalized[key] = {
        ...color,
        hex: hex && !hex.startsWith('#') ? '#' + hex : hex
      };
    });

    return normalized;
  }

  hasAnyConfiguracaoCha(configuracaoCha: Record<string, { visivel?: boolean }>): boolean {
    return Object.values(configuracaoCha).some((config) => config.visivel);
  }
}

